// Imports ------------------------------------------------------------------->

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

const REQUEST_TIMEOUT = 10000;

export class FetchError extends Error {
	private _body: any;
	private _status: number;

	constructor(message: string, status: number, body?: any) {
		super(message);
		this._body = body;
		this._status = status;
		/* Manually set the prototype for this object to FetchError. This was broken as of Typescript 2.1. See:
		 *https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work*/
		(this as any).__proto__ = FetchError.prototype;
	}

	get status() {
		return this._status;
	}

	get body() {
		return this._body;
	}
}

export interface FetchOptions {
	url: string;
	method?: string;
	headers?: {
		[key: string]: string
	}[];
	body?: any;
}

export function fetchImage(url: string, crossOrigin?: boolean) {
	return new Promise<HTMLImageElement>((resolve, reject) => {
		const img = document.createElement("img");
		if (crossOrigin)
			img.crossOrigin = "anonymous";
		img.onload = function() { resolve(img); };
		img.src = url;
	});
}

export function fetchJSON<T>(req: string | FetchOptions) {
	const opts = _prepareHeaders(req);
	return fetch<T>(opts);
}

export function fetchRejectJSON<T>(req: string | FetchOptions) {
	const opts = _prepareHeaders(req);
	return fetchReject<T>(opts);
}

export function fetch<T>(req: string | FetchOptions, acceptableStatusCodes = [200]) {
	return _fetch<T>(req, acceptableStatusCodes, _processOnload);
}

export function fetchReject<T>(req: string | FetchOptions, acceptableStatusCodes = [200]) {
	return _fetch<T>(req, acceptableStatusCodes, _processOnloadWithReject);
}

function _processOnload<T>(acceptableStatusCodes: Array<number>,
							request: XMLHttpRequest,
							resolve: Function,
							reject: Function) {
	return () => {
		if (acceptableStatusCodes.indexOf(request.status) === -1) {
			reject(new FetchError(`Server returned unexpected status code ${request.status}`, request.status, request.responseText));
		} else if (request.responseText) {
			resolve(JSON.parse(request.responseText));
		} else {
			resolve();
		}
	};
}

function _processOnloadWithReject<T>(acceptableStatusCodes: Array<number>,
										request: XMLHttpRequest,
										resolve: Function,
										reject: Function) {
	return () => {
		if (acceptableStatusCodes.indexOf(request.status) === -1) {
			reject(new FetchError(`Server returned unexpected status code ${request.status}`, request.status, request.responseText));
		} else if (request.responseText) {
			try {
				resolve(JSON.parse(request.responseText));
			} catch (e) {
				reject(e);
			}
		} else {
			resolve();
		}
	};
}

function _fetch<T>(req: string | FetchOptions,
					acceptableStatusCodes: Array<number>,
					onOnload: Function, faildAttempst: number = 5) {
	const opts: FetchOptions = (typeof req === "string") ? {url: req} : req;
	const method = opts.method || "GET";
	return new Promise<T>((resolve: Function, reject: Function) => {
		const request = new XMLHttpRequest;
		request.open(method, opts.url, true);
		if (opts.headers) {
			opts.headers.forEach((header) => {
				const key = Object.keys(header)[0];
				request.setRequestHeader(key, header[key]);
			});
		}
		request.timeout = REQUEST_TIMEOUT; // timeout must be set after calling open() otherwise IE11 will barf
		request.onload = onOnload(acceptableStatusCodes, request, resolve, reject);

		function retryRequest() {
			if (faildAttempst > 0) {
				faildAttempst--;
				_fetch(req, acceptableStatusCodes, onOnload, faildAttempst)
						.then((data: any) => resolve(data));
			} else {
				reject(new FetchError(`Server returned ${request.status}. After ${faildAttempst} failed attempts`, 400));
			}
		}

		request.onerror = () => {
			retryRequest();
		};
		request.ontimeout = () => {
			retryRequest();
		};
		request.send(opts.body);
	});
}

function _prepareHeaders(req: string | FetchOptions) {
	const opts: FetchOptions = (typeof req === "string") ? {url: req} : req;
	if (!opts.headers) {
		opts.headers = [
			{"Accept": "application/json"}
		];
	} else {
		const acceptHeaders = opts.headers.filter((header) => {
			return Object.keys(header)[0].toLowerCase() === "accept";
		});
		if (acceptHeaders.length === 0) {
			opts.headers.push({"Accept": "application/json"});
		}
	}
	return opts;
}
